import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type {
  IntegrationKindPatch,
  IntegrationKindRow,
  IntegrationKindWrite,
  UUID,
} from './types';

/** Nach jeder Mutation muss auch der Personalbedarf neu geladen werden —
 *  die bewilligten Stunden gehen dort in `h_woche` ein. */
function invalidate(qc: ReturnType<typeof useQueryClient>, planId: UUID): void {
  void qc.invalidateQueries({ queryKey: queryKeys.belegung.integrationKinder(planId) });
  void qc.invalidateQueries({ queryKey: queryKeys.belegung.personalbedarfAlle(planId) });
  void qc.invalidateQueries({ queryKey: queryKeys.belegung.monate(planId) });
  void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
  // Die Integrationspauschale zaehlt genau diese Kinder.
  void qc.invalidateQueries({
    queryKey: queryKeys.einnahmen.landAufschluesselung(planId),
  });
}

export function useIntegrationKinder(planId: UUID | null) {
  return useQuery({
    queryKey: planId
      ? queryKeys.belegung.integrationKinder(planId)
      : ['integrationKinder', 'none'],
    queryFn: async (): Promise<IntegrationKindRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<IntegrationKindRow[]>(
        `/plan/${planId}/integrationskinder`,
      );
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreateIntegrationKind(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IntegrationKindWrite): Promise<IntegrationKindRow> => {
      const res = await apiClient.post<IntegrationKindRow>(
        `/plan/${planId}/integrationskinder`,
        body,
      );
      return res.data;
    },
    onSuccess: () => invalidate(qc, planId),
  });
}

export function useUpdateIntegrationKind(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: UUID;
      patch: IntegrationKindPatch;
    }): Promise<IntegrationKindRow> => {
      const res = await apiClient.patch<IntegrationKindRow>(
        `/plan/${planId}/integrationskinder/${vars.id}`,
        vars.patch,
      );
      return res.data;
    },
    onSuccess: () => invalidate(qc, planId),
  });
}

export function useDeleteIntegrationKind(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/integrationskinder/${id}`);
    },
    onSuccess: () => invalidate(qc, planId),
  });
}
